/**
 * Almacenamiento durable (IndexedDB) para las grabaciones de backup de
 * llamadas manuales. Guarda los chunks del MediaRecorder a medida que llegan,
 * de modo que si el navegador se recarga o crashea a mitad de la llamada, lo
 * ya grabado sobrevive y el uploader en segundo plano puede subirlo después.
 *
 * Dos object stores:
 *  - recordings: metadata por llamada (keyPath 'id' = call_uuid).
 *  - chunks: los blobs del MediaRecorder (autoIncrement), indexados por recordingId.
 */

const DB_NAME = 'callRecordingBackup';
const DB_VERSION = 1;
const STORE_META = 'recordings';
const STORE_CHUNKS = 'chunks';

let dbPromise = null;

function openDB() {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
        const chunks = db.createObjectStore(STORE_CHUNKS, { keyPath: 'chunkId', autoIncrement: true });
        chunks.createIndex('recordingId', 'recordingId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

/** Crea la metadata de una grabación en curso. */
export async function createRecording(meta) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_META], 'readwrite');
    t.objectStore(STORE_META).put({
      ...meta,
      status: 'recording',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Guarda un chunk y refresca updatedAt (sirve para detectar grabaciones huérfanas). */
export async function appendChunk(recordingId, seq, blob) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_CHUNKS, STORE_META], 'readwrite');
    t.objectStore(STORE_CHUNKS).add({ recordingId, seq, blob });
    const metaStore = t.objectStore(STORE_META);
    const getReq = metaStore.get(recordingId);
    getReq.onsuccess = () => {
      const meta = getReq.result;
      if (meta) {
        meta.updatedAt = Date.now();
        metaStore.put(meta);
      }
    };
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Marca la grabación como terminada (lista para subir). */
export async function markComplete(recordingId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_META], 'readwrite');
    const store = t.objectStore(STORE_META);
    const getReq = store.get(recordingId);
    getReq.onsuccess = () => {
      const meta = getReq.result;
      if (meta) {
        meta.status = 'complete';
        meta.updatedAt = Date.now();
        store.put(meta);
      }
    };
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Devuelve la metadata de todas las grabaciones almacenadas. */
export async function listRecordings() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_META], 'readonly');
    const req = t.objectStore(STORE_META).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

/** Devuelve los chunks de una grabación ordenados por secuencia. */
export async function getChunks(recordingId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_CHUNKS], 'readonly');
    const index = t.objectStore(STORE_CHUNKS).index('recordingId');
    const req = index.getAll(IDBKeyRange.only(recordingId));
    req.onsuccess = () => {
      const rows = (req.result || []).slice().sort((a, b) => a.seq - b.seq);
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Prueba que IndexedDB esté disponible y escribible (falla en modo incógnito
 * con storage bloqueado, cuota llena, etc.). Escribe y borra un registro
 * temporal. Lanza si algo falla; se usa en el pre-flight antes de llamar.
 */
export async function selfTest() {
  const db = await openDB();
  const testId = '__preflight__';
  await new Promise((resolve, reject) => {
    const t = db.transaction([STORE_META], 'readwrite');
    t.objectStore(STORE_META).put({
      id: testId, status: 'preflight', createdAt: Date.now(), updatedAt: Date.now(),
    });
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('IndexedDB transaction aborted'));
  });
  await new Promise((resolve, reject) => {
    const t = db.transaction([STORE_META], 'readwrite');
    t.objectStore(STORE_META).delete(testId);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/** Borra la metadata y todos los chunks de una grabación (tras subirla con éxito). */
export async function deleteRecording(recordingId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_CHUNKS, STORE_META], 'readwrite');
    t.objectStore(STORE_META).delete(recordingId);
    const index = t.objectStore(STORE_CHUNKS).index('recordingId');
    const cursorReq = index.openCursor(IDBKeyRange.only(recordingId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}



// WEBPACK FOOTER //
// ./src/services/callRecording/db.js